import { usePropertyPageQuery } from '../../../../graphql/usePropertyPageQuery';
import { DualCTABanner } from './subcomponents/DualCTABanner';
import { SingleCTABanner } from './subcomponents/SingleCTABanner';
import { ResultOf } from 'gql.tada';
import { propertyPageGovernmentOverlaysBrickFragment } from '../../../../graphql/propertyPageQuery';
import { getGovernmentDataPartnerBannerImage } from '../../helpers/getGovernmentDataPartnerBannerImage';
import { GovernmentDataPartnerBannerWithVariant } from '../GovernmentDataPartnerBannerWithData/GovernmentDataPartnerBannerWithVariant';
import { ObservedElement } from '../../../../../app-kit/components/ObservedElement/ObservedElement';
import { DataGetter, trackEvent } from '@rea-argonaut/analytics';
import { deepOmitTypeName } from '../../../../../analytics/helpers/deepOmitTypeName';

type GovernmentOverlaysBrickResult = ResultOf<typeof propertyPageGovernmentOverlaysBrickFragment>;
type PartnerBannerV2 = NonNullable<
  NonNullable<GovernmentOverlaysBrickResult['governmentDataV3']>['partnerBannerV2']
>;
type BannerDetails = NonNullable<PartnerBannerV2['bannerDetails']>;
export type DualCTABannerDetails = Extract<BannerDetails, { ctaHeadline: unknown }>;
export type SingleCTABannerDetails = Extract<BannerDetails, { link: unknown }>;

const trackGovernmentDataPartnerCtaImpression = (
  impressionTracking: BannerDetails['impressionTracking'],
) => {
  if (!impressionTracking) return;
  const event: DataGetter = () => deepOmitTypeName(impressionTracking);

  trackEvent(event);
};

export const GovernmentDataPartnerBannerWithDataV2 = () => {
  const propertyPage = usePropertyPageQuery()?.data.propertyPageBySlugV3;
  const partnerBanner = propertyPage?.governmentDataV3?.partnerBanner;
  const partnerBannerV2 = propertyPage?.governmentDataV3?.partnerBannerV2;

  if (!partnerBannerV2 || !partnerBanner) return null;

  // Temporary -- Use old partnerBanner field to render old NT banner, since we haven't got the new NT variant yet
  const { partnerBannerVariant, isPropertyNorthernTerritory, state } = partnerBanner;
  if (isPropertyNorthernTerritory) {
    return (
      <GovernmentDataPartnerBannerWithVariant
        partnerBannerVariant={partnerBannerVariant}
        isPropertyNorthernTerritory={isPropertyNorthernTerritory}
        state={state}
      />
    );
  }

  const { isDualCTA, bannerDetails } = partnerBannerV2;
  const imageSource = getGovernmentDataPartnerBannerImage(bannerDetails.imageVariant);

  return (
    <ObservedElement
      onFirstIntersect={() =>
        trackGovernmentDataPartnerCtaImpression(bannerDetails.impressionTracking)
      }
    >
      {isDualCTA ? (
        <DualCTABanner
          bannerDetails={bannerDetails as DualCTABannerDetails}
          imageSource={imageSource}
        />
      ) : (
        <SingleCTABanner
          bannerDetails={bannerDetails as SingleCTABannerDetails}
          imageSource={imageSource}
        />
      )}
    </ObservedElement>
  );
};
